import React, { useEffect, useState, useRef } from 'react';
import { Plus, Headphones, Play, Pause, Upload, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { audioService } from '../services';
import mediaService from '../services/mediaService';
import { AudioBook } from '@brovicms/models';
import AudioPreviewModal from '../components/audio/AudioPreviewModal';

const AudioBooks = () => {
  const navigate = useNavigate();
  const [audios, setAudios] = useState<AudioBook[]>([]);
  const [currentAudioId, setCurrentAudioId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string>('');
  const [previewFileName, setPreviewFileName] = useState<string>('');
  useEffect(() => {
    const fetchAudios = async () => {
      const result = await audioService.getAudioBooks({isPublished: true});
      setAudios(result.data as unknown as AudioBook[]);
    }
    fetchAudios();
  }, []);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const handlePlayClick = (audio: AudioBook) => {
    const url = audio.file?.url || '';
    if (!url) return;

    // If modal already open for same audio, close it
    if (showPreviewModal && currentAudioId === audio.id) {
      setShowPreviewModal(false);
      setCurrentAudioId(null);
      setIsPlaying(false);
      setPreviewAudioUrl('');
      setPreviewFileName('');
      return;
    }

    setPreviewAudioUrl(url);
    setPreviewFileName(audio.file?.fileName || audio.title || 'Audio');
    setCurrentAudioId(audio.id);
    setShowPreviewModal(true);
  };

  useEffect(() => {
    // no-op cleanup currently
    return () => {};
  }, []);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading text-primary-800">Audio Books</h1>
          <p className="text-gray-600 mt-1">
            Manage your audio book collection
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/audio/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Audio Book
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-apple">
                <Headphones className="w-5 h-5 text-primary-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Audio Books</p>
                <p className="text-lg font-semibold text-gray-900">{audios.length}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-success-100 rounded-apple">
                <Play className="w-5 h-5 text-success-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-lg font-semibold text-gray-900">
                  {audios.filter(audio => audio.isPublished).length}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-warning-100 rounded-apple">
                <Pause className="w-5 h-5 text-warning-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-lg font-semibold text-gray-900">
                  {audios.filter(audio => audio.isPublished).length}
                </p>
              </div>
            </div>
          </div>
        </div> */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-error-100 rounded-apple">
                <Upload className="w-5 h-5 text-error-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Size</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatFileSize(audios.reduce((total, audio) => total + (audio.file?.fileSize || 0), 0))}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audio Books Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Audio Book</th>
                  <th>Author</th>
                  <th>Narrator</th>
                  <th>Duration</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {audios.map((audio) => (
                  <tr key={audio.id}>
                    <td>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-200 rounded-apple flex items-center justify-center mr-4">
                          {audio.coverImage ? (
                            <img
                              src={audio.coverImage.url}
                              alt={audio.title}
                              className="w-full h-full object-cover rounded-apple"
                            />
                          ) : (
                            <Headphones className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{audio.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {audio.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-gray-900">{audio.author}</td>
                    <td className="text-sm text-gray-900">{audio.narrator}</td>
                    <td className="text-sm text-gray-900">{formatDuration(audio.file?.duration || 0)}</td>
                    <td className="text-sm text-gray-900">{formatFileSize(audio.file?.fileSize || 0)}</td>
                    <td>
                      {audio.isPublished ? (
                        <span className="badge badge-success">Published</span>
                      ) : (
                        <span className="badge badge-warning">Draft</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1 text-gray-400 hover:text-primary-600"
                          onClick={() => handlePlayClick(audio)}
                          aria-label={currentAudioId === audio.id && isPlaying ? 'Pause' : 'Play'}
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1 text-gray-400 hover:text-error-600"
                          onClick={async () => {
                            const confirmed = window.confirm('Are you sure you want to delete this audio? This will remove the record and any uploaded files.');
                            if (!confirmed) return;
                            try {
                              // find audio
                              const target = audios.find(a => a.id === audio.id);
                              // attempt to delete associated storage files if present using mediaService
                              if (target) {
                                const audioUrl = (target as any).file?.url || (target as any).audioUrl || '';
                                const coverUrl = (target as any).coverImage?.url || (target as any).coverImageUrl || '';
                                const tryDeleteByUrl = async (fileUrl: string) => {
                                  if (!fileUrl) return;
                                  try {
                                    const urlObj = new URL(fileUrl);
                                    const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
                                    if (pathMatch) {
                                      const filePath = decodeURIComponent(pathMatch[1]);
                                      await mediaService.deleteFile(filePath);
                                    }
                                  } catch (e) {
                                    // ignore parsing/deletion errors
                                  }
                                };

                                await tryDeleteByUrl(audioUrl);
                                await tryDeleteByUrl(coverUrl);
                              }

                              await audioService.deleteAudio(audio.id);
                              setAudios(prev => prev.filter(a => a.id !== audio.id));
                            } catch (err) {
                              console.error('Failed to delete audio', err);
                            }
                          }}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {/* <button className="p-1 text-gray-400 hover:text-primary-600">
                          <Upload className="w-4 h-4" />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <AudioPreviewModal
        isOpen={showPreviewModal}
        audioUrl={previewAudioUrl}
        fileName={previewFileName}
        onConfirm={() => setShowPreviewModal(false)}
        onCancel={() => setShowPreviewModal(false)}
        showActions={false}
        isUploading={false}
      />
    </div>
  );
};

export default AudioBooks;
